import { useState, useEffect, useRef } from "react";

const TRACKER_URL = process.env.REACT_APP_DESKTOP_TRACKER_URL || "http://127.0.0.1:4567/status";

/**
 * Hook to connect to the local EBS Desktop Activity Tracker agent.
 * Tracks global mouse/keyboard activity across the entire OS.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether polling is active (e.g., user is checked in and not on break)
 * @param {Function} options.onInactive - Callback when user becomes inactive on the OS
 * @param {Function} options.onActive - Callback when user resumes activity
 * @param {number} options.pollIntervalMs - Polling interval in ms (default: 2000)
 */
export const useDesktopActivityTracker = ({
  enabled = true,
  onInactive,
  onActive,
  pollIntervalMs = 2000,
} = {}) => {
  const [trackerState, setTrackerState] = useState({
    isConnected: false,
    isActive: true,
    idleSeconds: 0,
    thresholdSeconds: 300,
  });

  const wasActiveRef = useRef(true);
  const inactiveTriggeredRef = useRef(false);
  const onInactiveRef = useRef(onInactive);
  const onActiveRef = useRef(onActive);

  useEffect(() => {
    onInactiveRef.current = onInactive;
  }, [onInactive]);

  useEffect(() => {
    onActiveRef.current = onActive;
  }, [onActive]);

  useEffect(() => {
    if (!enabled) {
      inactiveTriggeredRef.current = false;
      return;
    }

    let isMounted = true;
    let pollTimer = null;

    const checkActivity = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1200);

        const res = await fetch(TRACKER_URL, {
          method: "GET",
          signal: controller.signal,
          headers: {
            "Accept": "application/json",
          },
        });
        clearTimeout(timeoutId);

        if (!res.ok) {
          throw new Error(`Tracker responded with status ${res.status}`);
        }

        const data = await res.json();
        if (!isMounted) return;

        setTrackerState({
          isConnected: true,
          isActive: Boolean(data.is_active),
          idleSeconds: Number(data.idle_seconds || 0),
          thresholdSeconds: Number(data.threshold_seconds || 300),
        });

        // Handle transitions
        if (!data.is_active) {
          if (!inactiveTriggeredRef.current) {
            inactiveTriggeredRef.current = true;
            if (onInactiveRef.current) {
              onInactiveRef.current(data);
            }
          }
        } else {
          if (inactiveTriggeredRef.current || !wasActiveRef.current) {
            inactiveTriggeredRef.current = false;
            if (onActiveRef.current) {
              onActiveRef.current(data);
            }
          }
        }

        wasActiveRef.current = Boolean(data.is_active);
      } catch (err) {
        if (!isMounted) return;
        setTrackerState((prev) => ({
          ...prev,
          isConnected: false,
        }));
      }
    };

    // Run initial check and set interval
    checkActivity();
    pollTimer = setInterval(checkActivity, pollIntervalMs);

    return () => {
      isMounted = false;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [enabled, pollIntervalMs]);

  return trackerState;
};

export default useDesktopActivityTracker;
